import React from 'react';
import { Video, ShieldCheck, Globe2, Clock, Stethoscope, CalendarCheck, MessageCircle, UserPlus } from 'lucide-react';
import { PORTAL_URL } from '../SiteLayout';
import PageSEO from '../../components/PageSEO';

const WHY_TELE = [
  { icon: <Stethoscope size={22} />, title: 'Accéder à des spécialistes', text: "Consultez des médecins spécialisés même lorsque leur expertise n'est pas disponible dans votre région." },
  { icon: <Clock size={22} />, title: 'Gagner du temps', text: "Évitez des déplacements parfois longs et coûteux pour un avis médical lorsque celui-ci peut être réalisé à distance." },
  { icon: <UserPlus size={22} />, title: 'Faciliter le suivi', text: "Bénéficiez d'un suivi régulier après une intervention, une hospitalisation ou dans le cadre d'une maladie chronique." },
  { icon: <Globe2 size={22} />, title: 'Expertise nationale et internationale', text: "Nous développons un réseau permettant, avec votre accord, de solliciter des spécialistes exerçant dans d'autres provinces ou à l'étranger." },
  { icon: <ShieldCheck size={22} />, title: 'Une plateforme sécurisée', text: "Les échanges sont réalisés dans le respect de la confidentialité des données de santé et des règles déontologiques." },
];

const STEPS = [
  { n: '1', title: 'Demande de rendez-vous', text: "Vous choisissez le médecin ou la spécialité souhaitée et indiquez le motif de votre consultation." },
  { n: '2', title: 'Validation', text: "Le Centre vérifie que votre situation est compatible avec une téléconsultation et vous propose un créneau." },
  { n: '3', title: 'Préparation', text: "Vous recevez un lien sécurisé ainsi que les éventuelles consignes et documents à transmettre avant la consultation." },
  { n: '4', title: 'Consultation', text: "Le médecin échange avec vous par visioconférence, analyse votre situation et formule les recommandations adaptées." },
  { n: '5', title: 'Suivi', text: "Selon votre situation, vous recevrez un compte rendu, une ordonnance, une demande d'examens ou un rendez-vous de suivi." },
];

const CASES = [
  'Un avis médical spécialisé',
  'Un suivi de grossesse lorsque cela est approprié',
  "Le suivi d'une maladie chronique",
  'Le renouvellement de certains traitements',
  "L'interprétation d'examens complémentaires",
  'Un deuxième avis médical',
  'Le suivi post-opératoire',
  'Le conseil médical avant un déplacement vers le Centre',
];

const FAQS = [
  { q: 'Dois-je créer un compte ?', a: "Oui, afin de garantir la sécurité de votre dossier médical et de faciliter le suivi de vos consultations." },
  { q: 'Puis-je être accompagné pendant la consultation ?', a: 'Oui, si vous le souhaitez.' },
  { q: 'Vais-je recevoir une ordonnance ?', a: "Lorsque votre situation le justifie, le médecin pourra établir une ordonnance conformément à la réglementation applicable." },
  { q: 'La téléconsultation est-elle remboursée ?', a: "Les modalités de prise en charge dépendent de votre assurance, de votre employeur ou des conditions définies par le Centre." },
  { q: 'Puis-je envoyer mes examens avant la consultation ?', a: "Oui. Vous pouvez transmettre vos documents médicaux de manière sécurisée avant le rendez-vous." },
];

export default function SiteTele() {
  return (
    <main data-testid="site-tele">
      <PageSEO
        title="Téléconsultation médicale à distance — Matadi & Boma"
        description="Téléconsultation avec un médecin généraliste ou spécialiste depuis chez vous. Vidéo sécurisée, ordonnance envoyée, paiement Mobile Money. CMT Health Platform."
        path="/teleconsultation"
      />
      <div className="site-page-head">
        <div className="site-page-head-inner">
          <h1 className="site-reveal">L&apos;expertise médicale à portée de tous, où que vous soyez</h1>
          <p className="site-reveal-1">
            Grâce à <strong>CMT Health Platform</strong>, consultez un professionnel de santé sans vous déplacer
            lorsque votre situation médicale le permet. La téléconsultation facilite l&apos;accès aux soins spécialisés,
            réduit les contraintes de déplacement et permet de solliciter l&apos;expertise de médecins exerçant dans
            d&apos;autres régions ou à l&apos;international lorsque cela est nécessaire.
          </p>
          <div style={{ display: 'flex', gap: 10, marginTop: '1.5rem', flexWrap: 'wrap' }}>
            <a href={`${PORTAL_URL}?page=booking`} className="site-btn site-btn-primary" data-testid="tele-cta-booking-btn">
              <Video size={18} /> Demander une téléconsultation
            </a>
            <a href={`${PORTAL_URL}?page=login`} className="site-btn site-btn-outline" style={{ background: 'white' }} data-testid="tele-cta-account-btn">
              <UserPlus size={18} /> Créer mon espace patient
            </a>
          </div>
        </div>
      </div>

      {/* Qu'est-ce que */}
      <section className="site-section">
        <div style={{ maxWidth: 900, margin: '0 auto', textAlign: 'center' }}>
          <span className="site-kicker">Qu&apos;est-ce que la téléconsultation ?</span>
          <h2>Une consultation médicale à distance, en toute simplicité</h2>
          <p style={{ color: 'var(--site-muted)', lineHeight: 1.75 }}>
            La téléconsultation est une consultation réalisée à distance au moyen d&apos;un outil de communication
            sécurisé. Elle permet au médecin d&apos;évaluer votre situation, de répondre à vos questions, de vous
            orienter, de renouveler certains traitements lorsque cela est médicalement approprié et d&apos;assurer
            votre suivi. Si un examen clinique ou des investigations complémentaires sont nécessaires, une
            consultation présentielle vous sera proposée. <strong>La téléconsultation ne remplace pas la médecine
            traditionnelle : elle l&apos;enrichit</strong> en offrant une solution complémentaire adaptée à certaines situations.
          </p>
        </div>
      </section>

      {/* Pourquoi */}
      <div className="site-section-alt-wrap">
        <section className="site-section">
          <span className="site-kicker">Pourquoi choisir la téléconsultation ?</span>
          <h2>Une médecine plus proche de vous</h2>
          <div className="site-grid" style={{ marginTop: '2rem' }}>
            {WHY_TELE.map((w, i) => (
              <div className="site-card" key={i}>
                <div className="site-card-icon">{w.icon}</div>
                <h3>{w.title}</h3>
                <p>{w.text}</p>
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* Étapes */}
      <section className="site-section">
        <span className="site-kicker">Comment se déroule une téléconsultation ?</span>
        <h2>Votre consultation en cinq étapes</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginTop: '2rem' }}>
          {STEPS.map((s) => (
            <div key={s.n} className="site-card" style={{ textAlign: 'left', borderTop: '4px solid var(--site-primary)' }}>
              <div style={{ fontSize: 32, fontWeight: 800, color: 'var(--site-primary)' }}>{s.n}</div>
              <h3 style={{ marginTop: 6 }}>{s.title}</h3>
              <p style={{ fontSize: 14 }}>{s.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Quels cas */}
      <div className="site-section-alt-wrap">
        <section className="site-section">
          <span className="site-kicker">Dans quels cas est-elle adaptée ?</span>
          <h2>Quelques exemples</h2>
          <ul style={{ marginTop: '1.5rem', columnCount: 2, columnGap: '2.5rem', listStyle: 'none', padding: 0 }}>
            {CASES.map((c, i) => (
              <li key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: '.85rem', breakInside: 'avoid' }}>
                <ShieldCheck size={18} color="var(--site-primary)" style={{ flexShrink: 0, marginTop: 3 }} />
                <span>{c}</span>
              </li>
            ))}
          </ul>
          <p style={{ marginTop: '1rem', fontStyle: 'italic', color: '#7f1d1d' }}>
            La téléconsultation <strong>n&apos;est pas adaptée aux situations d&apos;urgence</strong> ou nécessitant un examen clinique immédiat.
          </p>
        </section>
      </div>

      {/* Confidentialité */}
      <section className="site-section">
        <div style={{ maxWidth: 900, margin: '0 auto', textAlign: 'center' }}>
          <span className="site-kicker">Confidentialité et sécurité</span>
          <h2>Vos données de santé sont protégées</h2>
          <p style={{ color: 'var(--site-muted)', lineHeight: 1.75 }}>
            Le respect de la confidentialité constitue une priorité du Centre Médical la Trinité. Les téléconsultations
            sont réalisées au moyen de solutions sécurisées répondant aux exigences de protection des données médicales.
            Les informations échangées sont intégrées à votre dossier médical et ne sont accessibles qu&apos;aux professionnels
            autorisés participant à votre prise en charge. Chaque téléconsultation est soumise aux mêmes obligations
            éthiques et déontologiques qu&apos;une consultation réalisée au cabinet.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <div className="site-section-alt-wrap">
        <section className="site-section">
          <span className="site-kicker">Questions fréquentes</span>
          <h2>Vos questions, nos réponses</h2>
          <div style={{ marginTop: '1.5rem', display: 'grid', gap: 10 }}>
            {FAQS.map((f, i) => (
              <details key={i} style={{ padding: '1rem 1.25rem', background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0' }}>
                <summary style={{ cursor: 'pointer', fontWeight: 600 }}>{f.q}</summary>
                <p style={{ marginTop: '.5rem', color: 'var(--site-muted)' }}>{f.a}</p>
              </details>
            ))}
          </div>
        </section>
      </div>

      {/* CTA */}
      <section className="site-section">
        <div className="site-cta-band">
          <div>
            <h2>L&apos;expertise médicale n&apos;a plus de frontières</h2>
            <p>Que vous soyez à Matadi, à Boma, ailleurs en RDC ou à l&apos;étranger, prenez rendez-vous pour une téléconsultation.</p>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <a href={`${PORTAL_URL}?page=booking`} className="site-btn site-btn-outline" style={{ background: 'white' }}>
              <Video size={18} /> Demander une téléconsultation
            </a>
            <a href="/contact" className="site-btn site-btn-outline" style={{ background: 'white' }}>
              <MessageCircle size={18} /> Nous contacter
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
